import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2, MessageSquare, ShieldAlert, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

import { api } from "../../lib/api";

const METHOD_OPTIONS = [
  { value: "verbal", label: "Verbal confirmation" },
  { value: "written", label: "Written confirmation" },
  { value: "digital_form", label: "Digital consent form" },
];

const SOURCE_OPTIONS = [
  { value: "in_person", label: "In person" },
  { value: "phone_call", label: "Phone call" },
  { value: "written_message", label: "Written message" },
  { value: "customer_portal", label: "Customer portal" },
];

const errorMessage = (error, fallback) => {
  const detail = error?.response?.data?.detail;
  return detail?.message || detail?.error || fallback;
};

export default function SmsPermissionCard({ projectId }) {
  const [contacts, setContacts] = useState([]);
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [method, setMethod] = useState("verbal");
  const [source, setSource] = useState("in_person");
  const [affirmed, setAffirmed] = useState(false);
  const [loading, setLoading] = useState(true);
  const [pending, setPending] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await api.get(`/projects/${projectId}/sms-permission`);
      const nextContacts = data?.contacts || [];
      setContacts(nextContacts);
      setSelectedCustomerId((current) => (
        nextContacts.some((contact) => contact.customer_id === current)
          ? current
          : (data?.selected_customer_id || nextContacts[0]?.customer_id || "")
      ));
    } catch (requestError) {
      setContacts([]);
      setError(errorMessage(requestError, "Could not load SMS consent."));
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const selected = useMemo(
    () => contacts.find((contact) => contact.customer_id === selectedCustomerId) || contacts[0],
    [contacts, selectedCustomerId],
  );
  const status = selected?.status || "not_granted";

  const onRecord = async () => {
    if (!selected || !affirmed) return;
    setPending(true);
    setError("");
    try {
      const { data } = await api.post(`/projects/${projectId}/sms-permission/grant`, {
        affirmation: true,
        customer_id: selected.customer_id,
        phone_e164: selected.phone_e164,
        method,
        source,
      });
      toast.success(data?.idempotent ? "SMS consent was already recorded." : "SMS consent recorded.");
      setAffirmed(false);
      await load();
    } catch (requestError) {
      const message = errorMessage(requestError, "Could not record SMS consent.");
      setError(message);
      toast.error(message);
    } finally {
      setPending(false);
    }
  };

  const onRevoke = async () => {
    if (!selected) return;
    setPending(true);
    setError("");
    try {
      await api.post(`/projects/${projectId}/sms-permission/revoke`, {
        customer_id: selected.customer_id,
        phone_e164: selected.phone_e164,
      });
      toast.success("SMS consent revoked.");
      await load();
    } catch (requestError) {
      const message = errorMessage(requestError, "Could not revoke SMS consent.");
      setError(message);
      toast.error(message);
    } finally {
      setPending(false);
    }
  };

  if (!projectId) return null;

  return (
    <section data-testid="sms-permission-card" className="mt-5 border-t border-[var(--jc-border)] pt-5">
      <div className="flex items-start gap-3">
        <MessageSquare size={18} className="mt-0.5 shrink-0 text-[var(--jc-text-soft)]" />
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h3 data-testid="sms-consent-title" className="text-sm font-semibold">
              Customer SMS consent
            </h3>
            {loading ? (
              <span data-testid="sms-consent-loading" className="inline-flex items-center gap-1 text-xs text-[var(--jc-text-soft)]">
                <Loader2 size={13} className="animate-spin" /> Loading
              </span>
            ) : status === "granted" ? (
              <span data-testid="sms-permission-status-granted" className="inline-flex items-center gap-1 rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs text-emerald-800">
                <ShieldCheck size={12} /> Recorded
              </span>
            ) : status === "opted_out" ? (
              <span data-testid="sms-permission-status-opted-out" className="inline-flex items-center gap-1 rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-xs text-rose-800">
                <ShieldAlert size={12} /> Customer opted out
              </span>
            ) : status === "revoked" ? (
              <span data-testid="sms-permission-status-revoked" className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-xs text-amber-800">
                Revoked
              </span>
            ) : (
              <span data-testid="sms-permission-status-not-granted" className="rounded-full border border-slate-200 bg-slate-50 px-2 py-0.5 text-xs text-slate-700">
                Not recorded
              </span>
            )}
          </div>

          <p data-testid="sms-consent-scope" className="mt-1 text-xs text-[var(--jc-text-soft)]">
            Transactional project-document texts only. Marketing messages are not authorized.
          </p>

          {error && (
            <p data-testid="sms-consent-error" role="alert" className="mt-3 text-xs text-rose-700">
              {error}
            </p>
          )}

          {!loading && contacts.length === 0 && !error && (
            <p data-testid="sms-consent-no-contact" className="mt-3 text-xs text-amber-800">
              Link an active customer contact with a valid phone number before recording consent.
            </p>
          )}

          {!loading && selected && (
            <div className="mt-4 space-y-3">
              <label className="block text-xs font-medium text-[var(--jc-text-soft)]">
                Customer contact
                <select
                  data-testid="sms-consent-customer-select"
                  value={selected.customer_id}
                  onChange={(event) => {
                    setSelectedCustomerId(event.target.value);
                    setAffirmed(false);
                    setError("");
                  }}
                  className="mt-1 w-full rounded-md border border-[var(--jc-border-strong)] bg-white px-3 py-2 text-sm text-[var(--jc-text)]"
                >
                  {contacts.map((contact) => (
                    <option
                      key={contact.contact_id}
                      value={contact.customer_id}
                      label={`${contact.customer_name} · ${contact.phone_masked || "phone needed"}`}
                    />
                  ))}
                </select>
              </label>

              <p data-testid="sms-permission-phone-masked" className="text-xs text-[var(--jc-text-soft)]">
                Verified customer phone: <span className="font-mono">{selected.phone_masked || "Unavailable"}</span>
              </p>

              {status === "opted_out" ? (
                <p data-testid="sms-permission-opt-out-notice" className="text-xs text-rose-700">
                  This opt-out remains in force. It cannot be overridden from this consent action.
                </p>
              ) : status === "granted" ? (
                <div className="flex flex-wrap items-center gap-3">
                  <p data-testid="sms-consent-recorded-evidence" className="text-xs text-[var(--jc-text-soft)]">
                    Method: {selected.method || "recorded"} · Source: {selected.source || "recorded"}
                  </p>
                  <button
                    type="button"
                    data-testid="sms-permission-revoke-button"
                    disabled={pending}
                    onClick={onRevoke}
                    className="text-xs font-semibold text-rose-700 transition-colors hover:text-rose-900 disabled:opacity-50"
                  >
                    {pending ? "Saving…" : "Revoke consent"}
                  </button>
                </div>
              ) : selected.phone_valid_e164 ? (
                <>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <label className="text-xs font-medium text-[var(--jc-text-soft)]">
                      Consent method
                      <select
                        data-testid="sms-consent-method-select"
                        value={method}
                        onChange={(event) => setMethod(event.target.value)}
                        className="mt-1 w-full rounded-md border border-[var(--jc-border-strong)] bg-white px-3 py-2 text-sm text-[var(--jc-text)]"
                      >
                        {METHOD_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value} label={option.label} />
                        ))}
                      </select>
                    </label>
                    <label className="text-xs font-medium text-[var(--jc-text-soft)]">
                      Consent source
                      <select
                        data-testid="sms-consent-source-select"
                        value={source}
                        onChange={(event) => setSource(event.target.value)}
                        className="mt-1 w-full rounded-md border border-[var(--jc-border-strong)] bg-white px-3 py-2 text-sm text-[var(--jc-text)]"
                      >
                        {SOURCE_OPTIONS.map((option) => (
                          <option key={option.value} value={option.value} label={option.label} />
                        ))}
                      </select>
                    </label>
                  </div>
                  <label className="flex items-start gap-2 text-xs text-[var(--jc-text-soft)]">
                    <input
                      type="checkbox"
                      data-testid="sms-permission-affirmation-checkbox"
                      checked={affirmed}
                      onChange={(event) => setAffirmed(event.target.checked)}
                      className="mt-0.5"
                    />
                    <span>I confirm this customer explicitly authorized transactional SMS at the verified number above.</span>
                  </label>
                  <button
                    type="button"
                    data-testid="sms-permission-grant-button"
                    disabled={!affirmed || pending}
                    onClick={onRecord}
                    className="jc-btn-primary inline-flex items-center gap-1.5 rounded-md px-3 py-2 text-xs font-semibold transition-[opacity,transform] hover:-translate-y-px disabled:translate-y-0 disabled:opacity-50"
                  >
                    {pending && <Loader2 size={13} className="animate-spin" />}
                    {pending ? "Recording…" : "Record SMS consent"}
                  </button>
                </>
              ) : (
                <p data-testid="sms-consent-invalid-phone" className="text-xs text-amber-800">
                  Update this customer’s phone before recording consent.
                </p>
              )}
            </div>
          )}
        </div>
      </div>
    </section>
  );
}